import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["id", "className", "isMultiOpen", "isToggle", "index", "defaultOpenItems", "onChange", "children", "mode"],
    _excluded2 = ["id", "className", "isOpen", "childIndex", "isDisabled", "onChange", "children", "title", "checkbox", "onClick", "pt", "pr", "pb", "pl"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { createContext, useContext, forwardRef, useRef, useState, Children, cloneElement, isValidElement } from 'react';
import cx from 'classnames';
import { ThemeProvider } from 'styled-components';
import { prefix } from '../../tools/settings';
import Checkbox from '../Checkbox';
import Collapsible from '../Collapsible';
import { Styled } from './Accordion.styles';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var Accordion = forwardRef(function (_ref, ref) {
  var _cx;

  var id = _ref.id,
      customClassName = _ref.className,
      _ref$isMultiOpen = _ref.isMultiOpen,
      isMultiOpen = _ref$isMultiOpen === void 0 ? true : _ref$isMultiOpen,
      _ref$isToggle = _ref.isToggle,
      isToggle = _ref$isToggle === void 0 ? true : _ref$isToggle,
      index = _ref.index,
      defaultOpenItems = _ref.defaultOpenItems,
      _onChange = _ref.onChange,
      children = _ref.children,
      _ref$mode = _ref.mode,
      mode = _ref$mode === void 0 ? 'light' : _ref$mode,
      rest = _objectWithoutProperties(_ref, _excluded);

  var className = cx((_cx = {}, _defineProperty(_cx, "".concat(prefix, "-accordion"), true), _defineProperty(_cx, customClassName, !!customClassName), _cx));

  var initializeState = function initializeState() {
    if (isMultiOpen) {
      return defaultOpenItems || [];
    } else {
      return defaultOpenItems || null;
    }
  };

  var getExpandCondition = function getExpandCondition(index, itemIndex) {
    if (Array.isArray(index)) {
      return index.includes(itemIndex);
    }

    return index === itemIndex;
  };

  var _useState = useState(initializeState),
      _useState2 = _slicedToArray(_useState, 2),
      expandedIndex = _useState2[0],
      setExpandedIndex = _useState2[1];

  var _useRef = useRef(index != null),
      isControlled = _useRef.current;

  var _index = isControlled ? index : expandedIndex;

  var clones = Children.map(children, function (child, childIndex) {
    if (!isValidElement(child)) return;
    return cloneElement(child, {
      isOpen: getExpandCondition(_index, childIndex),
      childIndex: childIndex,
      onChange: function onChange(isExpanded) {
        if (isMultiOpen) {
          if (isExpanded) {
            var newIndexes = [].concat(_toConsumableArray(_index), [childIndex]);
            !isControlled && setExpandedIndex(newIndexes);
            _onChange && _onChange(newIndexes);
          } else {
            var _newIndexes = _index.filter(function (itemIndex) {
              return itemIndex !== childIndex;
            });

            !isControlled && setExpandedIndex(_newIndexes);
            _onChange && _onChange(_newIndexes);
          }
        } else {
          if (isExpanded) {
            !isControlled && setExpandedIndex(childIndex);
            _onChange && _onChange(childIndex);
          } else {
            if (isToggle) {
              !isControlled && setExpandedIndex(null);
              _onChange && _onChange(null);
            }
          }
        }
      }
    });
  });
  return _jsx(ThemeProvider, {
    theme: {
      mode: mode
    },
    children: _jsx(Styled.Accordion, _objectSpread(_objectSpread({
      id: id,
      className: className,
      ref: ref,
      role: "presentation"
    }, rest), {}, {
      children: clones
    }))
  });
});
var AccordionItemContext = createContext();

var useAccordionItemContext = function useAccordionItemContext() {
  return useContext(AccordionItemContext);
};

var AccordionItem = forwardRef(function (_ref2, ref) {
  var _cx2;

  var id = _ref2.id,
      customClassName = _ref2.className,
      isOpen = _ref2.isOpen,
      childIndex = _ref2.childIndex,
      isDisabled = _ref2.isDisabled,
      onChange = _ref2.onChange,
      children = _ref2.children,
      title = _ref2.title,
      checkbox = _ref2.checkbox,
      onClick = _ref2.onClick,
      pt = _ref2.pt,
      pr = _ref2.pr,
      pb = _ref2.pb,
      pl = _ref2.pl,
      rest = _objectWithoutProperties(_ref2, _excluded2);

  var itemHeaderId = "".concat(prefix, "-accordion-header-").concat(childIndex);
  var itemContentId = "".concat(prefix, "-accordion-panel-").concat(childIndex);
  var className = cx((_cx2 = {}, _defineProperty(_cx2, "".concat(prefix, "-accordion__item"), true), _defineProperty(_cx2, customClassName, !!customClassName), _cx2));

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isExpanded = _useState4[0],
      setIsExpanded = _useState4[1];

  var _useRef2 = useRef(isOpen != null),
      isControlled = _useRef2.current;

  var _isExpanded = isControlled ? isOpen : isExpanded;

  var onToggle = function onToggle(event) {
    if (!isDisabled) {
      onChange && onChange(!_isExpanded);
      !isControlled && setIsExpanded(!isExpanded);
      onClick && onClick(event);
    }
  };

  return _jsx(AccordionItemContext.Provider, {
    value: {
      checkbox: checkbox,
      isExpanded: _isExpanded,
      itemHeaderId: itemHeaderId,
      itemContentId: itemContentId,
      isDisabled: isDisabled,
      onClick: onClick,
      title: title,
      onToggle: onToggle,
      pt: pt,
      pr: pr,
      pb: pb,
      pl: pl
    },
    children: _jsxs(Styled.AccordionItem, _objectSpread(_objectSpread({
      id: id,
      className: className,
      ref: ref
    }, rest), {}, {
      children: [_jsx(AccordionHeader, {}), _jsx(AccordionContent, {
        children: typeof children === 'function' ? children({
          isExpanded: _isExpanded,
          isDisabled: isDisabled
        }) : children
      })]
    }))
  });
});
var AccordionHeader = forwardRef(function (props, ref) {
  var _checkbox$isDisabled, _checkbox$id, _checkbox$isChecked, _checkbox$isIndetermi, _checkbox$isInvalid, _checkbox$size;

  var _useAccordionItemCont = useAccordionItemContext(),
      checkbox = _useAccordionItemCont.checkbox,
      isExpanded = _useAccordionItemCont.isExpanded,
      itemContentId = _useAccordionItemCont.itemContentId,
      itemHeaderId = _useAccordionItemCont.itemHeaderId,
      isDisabled = _useAccordionItemCont.isDisabled,
      title = _useAccordionItemCont.title,
      onToggle = _useAccordionItemCont.onToggle;

  var checkboxIsDisabled = isDisabled || ((_checkbox$isDisabled = checkbox === null || checkbox === void 0 ? void 0 : checkbox.isDisabled) !== null && _checkbox$isDisabled !== void 0 ? _checkbox$isDisabled : false);
  return _jsxs(Styled.AccordionHeader, {
    ref: ref,
    id: itemHeaderId,
    disabled: isDisabled,
    "aria-disabled": isDisabled,
    "aria-expanded": isExpanded,
    "aria-controls": itemContentId,
    children: [checkbox && _jsx(Checkbox, {
      className: "".concat(prefix, "-accordion-header-checkbox"),
      id: (_checkbox$id = checkbox === null || checkbox === void 0 ? void 0 : checkbox.id) !== null && _checkbox$id !== void 0 ? _checkbox$id : null,
      isChecked: (_checkbox$isChecked = checkbox === null || checkbox === void 0 ? void 0 : checkbox.isChecked) !== null && _checkbox$isChecked !== void 0 ? _checkbox$isChecked : false,
      isDisabled: checkboxIsDisabled,
      isIndeterminate: (_checkbox$isIndetermi = checkbox === null || checkbox === void 0 ? void 0 : checkbox.isIndeterminate) !== null && _checkbox$isIndetermi !== void 0 ? _checkbox$isIndetermi : false,
      isInvalid: (_checkbox$isInvalid = checkbox === null || checkbox === void 0 ? void 0 : checkbox.isInvalid) !== null && _checkbox$isInvalid !== void 0 ? _checkbox$isInvalid : false,
      onChange: checkbox === null || checkbox === void 0 ? void 0 : checkbox.onChange,
      size: (_checkbox$size = checkbox === null || checkbox === void 0 ? void 0 : checkbox.size) !== null && _checkbox$size !== void 0 ? _checkbox$size : 'medium'
    }), _jsxs(Styled.AccordionTitle, {
      isDisabled: isDisabled,
      onClick: function onClick(event) {
        return onToggle(event);
      },
      children: [title, _jsx(Styled.ToggleIcon, {
        icon: isExpanded ? 'chevron-up' : 'chevron-down'
      })]
    })]
  });
});
var AccordionContent = forwardRef(function (props, ref) {
  var _useAccordionItemCont2 = useAccordionItemContext(),
      isDisabled = _useAccordionItemCont2.isDisabled,
      isExpanded = _useAccordionItemCont2.isExpanded,
      itemContentId = _useAccordionItemCont2.itemContentId,
      itemHeaderId = _useAccordionItemCont2.itemHeaderId,
      pt = _useAccordionItemCont2.pt,
      pr = _useAccordionItemCont2.pr,
      pb = _useAccordionItemCont2.pb,
      pl = _useAccordionItemCont2.pl;

  return _jsx(Styled.AccordionContent, {
    isOpen: isExpanded,
    isDisabled: isDisabled,
    pt: pt,
    pr: pr,
    pb: pb,
    pl: pl,
    children: _jsx(Collapsible, _objectSpread({
      ref: ref,
      id: itemContentId,
      "aria-labelledby": itemHeaderId,
      "aria-hidden": !isExpanded,
      isOpen: isExpanded
    }, props))
  });
});
Accordion.Item = AccordionItem;
export default Accordion;